// "How it works" for the Learning loop folder: the four phases, every step,
// and which numbered prompt runs where. Clicking a prompt chip opens it in
// the editor on the left.

import React from 'react';
import { Box, Typography, Chip, Tooltip, useTheme, alpha, Divider } from '@mui/material';
import {
  Search as ListenIcon,
  Mic as RecordIcon,
  Send as PublishIcon,
  Insights as LearnIcon,
  ArrowDownward as DownIcon,
  Replay as LoopIcon,
} from '@mui/icons-material';
import { LOOP_PHASES, LOOP_PROMPTS, pNo } from './loopPromptMeta';
import LoopDiagram from './LoopDiagram';

const PHASE_ICONS = [<ListenIcon key="l" />, <RecordIcon key="r" />, <PublishIcon key="p" />, <LearnIcon key="m" />];
const ARROWS = ['The best questions become the brief', 'The transcript becomes the posts', 'Every post is measured'];

interface Props {
  /** Which prompt slugs exist in the database, so a chip can open the editor. */
  slugToId: Record<string, string>;
  onOpenPrompt: (id: string) => void;
}

const LearningLoopGuide: React.FC<Props> = ({ slugToId, onOpenPrompt }) => {
  const theme = useTheme();
  const byNumber = Object.fromEntries(LOOP_PROMPTS.map(p => [p.number, p]));

  const chip = (n: number) => {
    const p = byNumber[n];
    if (!p) return null;
    const id = slugToId[p.slug];
    return (
      <Tooltip key={n} title={<span><b>{pNo(n)} {p.name}</b><br />{p.role}<br /><i>{p.when}</i></span>}>
        <Chip
          size="small"
          label={`${pNo(n)} ${p.name}`}
          onClick={id ? () => onOpenPrompt(id) : undefined}
          sx={{
            fontWeight: 600, cursor: id ? 'pointer' : 'default',
            bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.dark,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>How the learning loop works</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Four jobs, run in a circle: listen for what the audience asks, record a vodcast that answers it, publish
        and talk back, measure and learn. Each step below says when it runs and which numbered prompt it uses.
        Every prompt in the list on the left is one of these numbers, so an edit here changes exactly that step.
      </Typography>

      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2, mb: 2 }}>
        <LoopDiagram />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {LOOP_PHASES.map((phase, i) => (
          <React.Fragment key={phase.title}>
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 700, fontSize: 13 }}>{i + 1}</Box>
                <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{PHASE_ICONS[i]}</Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{phase.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{phase.sub}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(220px, 1fr))' }, gap: 1.25 }}>
                {phase.steps.map(step => (
                  <Box key={step.title} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, p: 1.5, bgcolor: theme.palette.background.paper, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{step.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{step.what}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{step.when}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                      {step.prompts.length ? step.prompts.map(chip) : <Chip size="small" label="Rules, no prompt" variant="outlined" sx={{ color: 'text.secondary' }} />}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
            {i < LOOP_PHASES.length - 1 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, color: 'text.secondary', fontSize: 13 }}><DownIcon fontSize="small" /> {ARROWS[i]}</Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, color: theme.palette.primary.main, fontWeight: 600, fontSize: 13 }}><LoopIcon fontSize="small" /> The playbook and the next brief close the loop: back to Listen</Box>
            )}
          </React.Fragment>
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>Every prompt, in order</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {LOOP_PROMPTS.map(p => {
          const id = slugToId[p.slug];
          return (
            <Box key={p.slug} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr' }, gap: 1.5, py: 1.25, borderTop: `1px solid ${theme.palette.divider}`, alignItems: 'start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip size="small" label={pNo(p.number)} sx={{ fontFamily: 'monospace', fontWeight: 700, bgcolor: theme.palette.text.primary, color: theme.palette.background.paper }} />
                <Typography
                  variant="body2" fontWeight={700}
                  onClick={id ? () => onOpenPrompt(id) : undefined}
                  sx={{ cursor: id ? 'pointer' : 'default', '&:hover': id ? { color: theme.palette.primary.main } : undefined }}
                >
                  {p.name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">{p.role}</Typography>
                <Typography variant="caption" color="text.secondary"><b>When:</b> {p.when}</Typography>
                <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>{p.slug}{id ? '' : ' · not synced yet'}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Ranking, slot planning, duplicate checks, the reach floor, measurement and decisions use no prompt. They are rules, so they behave the same every time.
        A prompt that is "not synced yet" appears after "Sync with Braintrust".
      </Typography>
    </Box>
  );
};

export default LearningLoopGuide;
